/**
 * Sedi e sportelli nelle scelte dell'interfaccia (463).
 *
 * Uno sportello (presidio ASL, SAI, sportello comunale) e' una sede con la sede madre. Nelle
 * tendine compare sotto la sua sede, come «CIDIS Caserta › SAI CASERTA»: dove basta la sede si
 * sceglie la sede, lo sportello quando serve dire dove e' stato reso il servizio. Senza dipendenze
 * dal browser: le prove dell'API eseguono queste regole.
 */

/** Sede come arriva da GET /riferimenti/sedi. */
export type SedeElenco = {
  id: string
  nome: string
  codice?: string
  attiva?: boolean
  sede_padre_id?: string | null
  sede_padre_nome?: string | null
}

/** Incarico della sessione (GET /auth/io), per le parti che servono alla sede. */
export type IncaricoConSedi = { ambito: { tipo: string; id: string | null; sedi?: string[] } }

const confronta = (a: string, b: string) => a.localeCompare(b, 'it', { sensitivity: 'base' })

/** «CIDIS Caserta › SAI CASERTA» per uno sportello, il nome per una sede. */
export function nomeSede(s: SedeElenco): string {
  return s.sede_padre_id && s.sede_padre_nome ? `${s.sede_padre_nome} › ${s.nome}` : s.nome
}

/**
 * Ogni sede seguita dai suoi sportelli, in ordine di nome. Uno sportello la cui sede madre non e'
 * nell'elenco (per esempio perche' chiusa) resta comunque al posto della sua sede madre.
 */
export function inOrdineDiSede<T extends SedeElenco>(sedi: T[]): T[] {
  const gruppo = (s: T) => s.sede_padre_id
    ? { nome: s.sede_padre_nome ?? s.nome, id: s.sede_padre_id }
    : { nome: s.nome, id: s.id }
  return [...sedi].sort((a, b) => {
    const ga = gruppo(a)
    const gb = gruppo(b)
    return confronta(ga.nome, gb.nome) || ga.id.localeCompare(gb.id)
      || Number(!!a.sede_padre_id) - Number(!!b.sede_padre_id)
      || confronta(a.nome, b.nome) || a.id.localeCompare(b.id)
  })
}

/**
 * Voci di una tendina delle sedi, raggruppate. Con `chiuse` le sedi chiuse portano il segno: si
 * mostrano solo dove un dato le indica gia'.
 */
export function opzioniSedi(sedi: SedeElenco[], { chiuse = false } = {}): { valore: string; etichetta: string }[] {
  return inOrdineDiSede(sedi).map((s) => ({
    valore: s.id,
    etichetta: chiuse && s.attiva === false ? `${nomeSede(s)} (chiusa)` : nomeSede(s),
  }))
}

/** Voci per le caselle (progetti, enti): stesse etichette, forma { codice, nome }. */
export function vociSedi(sedi: SedeElenco[]): { codice: string; nome: string }[] {
  return opzioniSedi(sedi).map((o) => ({ codice: o.valore, nome: o.etichetta }))
}

/**
 * Sedi coperte da un incarico di sede: la sede e i suoi sportelli, come le calcola l'API all'accesso
 * (contesto.ts, sediCoperte). Per gli altri ambiti nessuna.
 */
export function sediDellIncarico(i: IncaricoConSedi): string[] {
  if (i.ambito.tipo !== 'sede' || !i.ambito.id) return []
  return i.ambito.sedi?.length ? i.ambito.sedi : [i.ambito.id]
}
